import React from 'react';
import { Stack, Typography, CircularProgress } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

export interface IconTextContentProps {
  /** The icon to display above the text */
  icon: React.ReactNode;
  /** The text to display below the icon */
  text: string;
  /** Whether to show loading spinner instead of icon */
  loading?: boolean;
  /** Custom text color (overrides inherit) */
  textColor?: string;
  /** Font weight for text */
  fontWeight?: number;
  /** Additional sx styles for the text */
  textSx?: SxProps<Theme>;
  /** Additional sx styles for the stack container */
  sx?: SxProps<Theme>;
}

/**
 * Shared component for rendering icon above text
 * Used by both IconOverTextButton and IconOverTextButtonStyledDropdown
 */
export const IconTextContent: React.FC<IconTextContentProps> = ({ icon, text, loading = false, textColor, fontWeight, textSx, sx }) => {
  return (
    <Stack direction='column' alignItems='center' spacing={0.5} sx={sx}>
      {/* Icon or Loading Spinner */}
      {loading ? (
        <CircularProgress size={20} sx={{ color: 'inherit' }} />
      ) : (
        React.isValidElement(icon) &&
        React.cloneElement(icon as React.ReactElement, {
          fontSize: 'small',
          sx: { color: 'inherit' },
        })
      )}
      {/* Text */}
      <Typography
        variant='caption'
        sx={{
          fontSize: '0.75rem',
          lineHeight: 1,
          color: textColor || 'inherit',
          whiteSpace: 'nowrap',
          ...(fontWeight && { fontWeight }),
          ...textSx,
        }}
      >
        {text}
      </Typography>
    </Stack>
  );
};

export default IconTextContent;
